<template>
	<div class="bottom-cash-payment">
		<AppBottomSheet :forceWidth="bottomSheetWidth" :active="active" @toggling-popup="v => $emit('toggling-popup', v)">
			<div class="bottom-cash-content" v-if="active">
				<ModalHeader :title="getString('cash')"
							 no-new-line
							 :button-text="getString('confirm')"
							 @click-button="confirmPayment" />
				<div class="container-payment-status">
					<div class="column-status">
						<div class="column-status-name">{{ getString('total') }}</div>
						<div class="column-status-value">{{ currencyStore.readableCurrencyValue(toPay) }}</div>
					</div>
					<div class="alignment-cta">
						<div class="column-status status-topay">
							<div class="column-status-name">{{ getString('cash') }}</div>
							<div class="column-status-value">{{ currencyStore.readableCurrencyValue(cashAmount) }}</div>
						</div>
						<div class="column-status status-paid">
							<div class="column-status-name">{{ getString('change') }}</div>
							<div class="column-status-value">{{ changeAmount < 0 ? '-' : currencyStore.readableCurrencyValue(changeAmount) }}</div>
						</div>
					</div>
				</div>
				<div class="container-body mt-5">
					<div class="container-info">
						<div class="container-shortcuts">
							<div class="shortcut" v-for="(paymentSuggestion, index) in paymentsSuggestionsToShow" :key="index" :class="paymentSuggestion.score" @click="applyShortcut(currencyStore.actualToDbValue(paymentSuggestion.amount))">{{ currencyStore.readableCurrencyValue(paymentSuggestion.amount, false) }}</div>
						</div>
					</div>
					<div class="container-numpad">
						<BottomNumberEditorNumpad ref="numpad" @change-number="changeNumber" :nothing-to-delete="nothingChanged" :startingNumber="0" no-actions />
					</div>
				</div>
			</div>
		</AppBottomSheet>
		<AppPopup ref="popup-confirm-amount" :title="getString('warning_confirm_amount')" :body-text="getString('warning_confirm_amount_question', [currencyStore.readableCurrencyValue(cashAmount), currencyStore.readableCurrencyValue(toPay)])" :buttons="[{text: getString('cancel'), negative: true, callback: hidePopup}, {text: getString('continue'), callback: forcePayment}]" />
	</div>
</template>

<script>
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import WindowWidth from "@/console-new/mixins/WindowWidth";
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import {useCurrencyStore, useLanguageStore} from "@/shared/stores";
import {useCounterStore} from "@/console-new/stores";
import BottomNumberEditorNumpad from "@/console-new/components/ops/counter/BottomNumberEditorNumpad.vue";
import BottomNumberEditorShortcuts from "@/console-new/components/ops/counter/BottomNumberEditorShortcuts.vue";
import AppPopup from "@/shared/components/AppPopup.vue";
import AppLoader from "@/shared/components/AppLoader.vue";

export default {
	setup() {
		let languageStore = useLanguageStore();
		const counterStore = useCounterStore();
		const currencyStore = useCurrencyStore();
		return {getString: languageStore.getString, languageStore, currencyStore, counterStore};
	},
	name: "BottomCashPayment",
	components: {AppLoader, AppPopup, BottomNumberEditorShortcuts, BottomNumberEditorNumpad, ModalHeader, AppBottomSheet},
	emits: ["toggling-popup", "payment-done"],
	mixins: [WindowWidth],
	props: {
		active: {
			type: Boolean,
			required: true,
		},
		toPay: {
			type: Number,
			required: true,
		}
	},
	data() {
		return {
			cashAmount: 0,
			paymentSuggestionsResults: [],
		}
	},
	computed: {
		bottomSheetWidth() {
			if(this.windowWidth < 800) {
				return "100%";
			} else {
				return "800px";
			}
		},
		maxSuggestions() {
			if(this.windowWidth <= 470) {
				return 4;
			} else {
				return 6;
			}
		},
		paymentsSuggestionsToShow() {
			return this.paymentSuggestionsResults.slice(0, this.maxSuggestions);
		},
		changeAmount() {
			return this.cashAmount - this.toPay;
		},
		nothingChanged() {
			return this.cashAmount === 0;
		},
	},
	methods: {
		confirmPayment(force = false) {
			if(!force && this.cashAmount > 0 && this.toPay > this.cashAmount) {
				this.$refs['popup-confirm-amount'].toggleStatus(true);
			} else {
				this.$emit("toggling-popup", false);
				this.$emit("payment-done", this.cashAmount);
			}
		},
		hidePopup() {
			this.$refs['popup-confirm-amount'].toggleStatus(false);
		},
		forcePayment() {
			this.hidePopup();
			this.confirmPayment(true);
		},
		changeNumber(number) {
			this.cashAmount = number;
		},
		applyShortcut(amount) {
			this.cashAmount = amount;
			if(this.$refs.numpad) {
				this.$refs.numpad.currentNumber = 0;
			}
		},
		clear() {
			this.cashAmount = 0;
			if(this.$refs.numpad) {
				this.$refs.numpad.currentNumber = 0;
			}
			this.paymentSuggestionsResults = [];
		},
		paymentSuggestions(amountToPay, params = {
			probMul: 0.9604338851830695,
			convDen: 8.654481683749598,
			convMul: 77.60130910378815,
			quantityPen: 0.558913436923395,
			bonusMul10: 0.8612230897983554,
			bonusMul5: 1.2946447456756829,
			changeBonus: 0.2509660533671945,
			overflowPenalty: 8.525577545540418
		}) {
			let probMapping = [
				{denomination: 200, prob: 1},
				{denomination: 100, prob: 2},
				{denomination: 50, prob: 6},
				{denomination: 20, prob: 10},
				{denomination: 10, prob: 8},
				{denomination: 5, prob: 9},
				{denomination: 2, prob: 7},
				{denomination: 1, prob: 8},
				{denomination: 0.5, prob: 7},
				{denomination: 0.2, prob: 6},
				{denomination: 0.1, prob: 5},
				{denomination: 0.05, prob: 4},
				{denomination: 0.02, prob: 3},
				{denomination: 0.01, prob: 2}
			];

			// Create a mapping for denomination to probability
			let probLookup = probMapping.reduce((acc, val) => {
				acc[val.denomination] = val.prob;
				return acc;
			}, {});

			// Function to find all combinations of notes/coins
			function findCombinations(amount, denominations, maxNotes, current, all, bestScores) {
				if (current.length > maxNotes) return; // Limit the number of notes/coins

				let total = 0;
				let prob = 0;
				current.forEach((val) => {
					total += val;
					prob += probLookup[val];
				});
				if (total >= amount) {
					current.sort((a, b) => b - a); // Sort the notes/coins in descending order
					let overflowPenalty = 0;
					let partial = 0;
					current.forEach((val) => {
						overflowPenalty += partial >= amount ? params.overflowPenalty : 0;
						partial += val;
					});
					prob /= current.length;
					prob *= params.probMul;
					let change = total - amount;
					let convenience = (1 / (change + params.convDen)) * params.convMul;
					let quantityPenalty = (current.length * current.length) * params.quantityPen;
					let bonusMul = total % 10 === 0 ? params.bonusMul10 : (total % 5 === 0 ? params.bonusMul5 : 0);
					let changeBonus = change === 0 ? params.changeBonus*10 : (probLookup.hasOwnProperty(change) ? probLookup[change]*params.changeBonus : 0);
					let score = prob + convenience + bonusMul + changeBonus - quantityPenalty - overflowPenalty;

					// Check if this combination is better than previous combinations for the same total amount
					if (!bestScores[total] || score > bestScores[total]) {
						all.push({amount: Number(total.toFixed(2)), score: score, prob: prob, conv: convenience, bonusMul: bonusMul, changeBonus: changeBonus, quant: -quantityPenalty, overflowPenalty: -overflowPenalty, raw: current});
						bestScores[total] = score; // Update the best score for this total amount
					}
					return;
				}

				for (let i = 0; i < denominations.length; i++) {
					findCombinations(amount, denominations, maxNotes, [...current, denominations[i]], all, bestScores);
				}
			}

			let combinations = [];
			let denominations = probMapping.map(v => v.denomination);
			findCombinations(amountToPay, denominations, 4, [], combinations, {});

			// remove duplicates, only keep the best score for each amount
			combinations = Object.values(combinations.reduce((acc, val) => {
				if (!acc.hasOwnProperty(val.amount) || acc[val.amount].score < val.score) {
					acc[val.amount] = val;
				}
				return acc;
			}, {}));
			let results = combinations.sort((a, b) => b.score - a.score).slice(0, 6);
			if(results.length === 0) {
				return [];
			}
			let maxScore = results[0].score;
			let scoreThreshold = maxScore * 0.55;
			let highThreshold = maxScore * 0.85;
			let mediumThreshold = maxScore * 0.7;

			return results.filter(v => v.score >= scoreThreshold).map(v => {
				if (v.score >= highThreshold) {
					return {amount: v.amount, score: "high"};
				} else if (v.score >= mediumThreshold) {
					return {amount: v.amount, score: "medium"};
				} else {
					return {amount: v.amount, score: "low"};
				}
			});
		}
	},
	watch: {
		active: function (newVal) {
			if(newVal && this.toPay > 0) {
				setTimeout(() => {
					this.paymentSuggestionsResults = this.paymentSuggestions(this.currencyStore.dbToActualValue(this.toPay));
				}, 760);
			} else {
				this.clear();
			}
		},
	}
}
</script>

<style scoped lang="scss">
.container-payment-status {
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: center;
	margin-top: 10px;
	gap: 5px;

	.alignment-cta {
		display: flex;
		width: 66.666%;
		gap: 5px;
		@media (max-width: 520px) {
			flex-direction: column;
			width: calc(100% / 2);
			min-height: 165px;
		}
	}
	.column-status {
		display: flex;
		flex-direction: column;
		justify-content: center;
		align-items: center;
		width: calc(100% / 3);
		min-height: 80px;
		padding: 10px;
		box-shadow: inset 0 1px 2px var(--color-box-shadow-3);
		border-radius: 13px;
		overflow: hidden;
		background-color: var(--color-bg);
		@media (max-width: 520px) {
			width: calc(100% / 2);
			min-height: 165px;
		}

		&-name {
			font-size: 14px;
		}

		&-value {
			font-size: 24px;
			font-weight: 500;
			margin-top: 4px;
		}

		&.status-paid {
			background-color: var(--color-bg-primary-2);
			width: calc(100% / 2);
			@media (max-width: 520px) {
				width: 100%;
				min-height: 80px;
			}
		}

		&.status-topay {
			background-color: var(--color-fill-button);
			width: calc(100% / 2);
			@media (max-width: 520px) {
				width: 100%;
				min-height: 80px;
			}
		}

		.column-status-name {
			@include truncateString(1);
			width: 100%;
			text-align: center;

			@media (max-width: 450px) {
				font-size: 13px;
			}
		}

		.column-status-value {
			@include truncateString(1);
			width: 100%;
			text-align: center;

			@media (max-width: 450px) {
				font-size: 20px;
			}
		}
	}
}

.container-body {
	display: flex;
	padding-top: 10px;

	.container-info {
		width: 50%;
		display: flex;
		flex-direction: column;

		.shortcut-title {
			font-size: 14px;
			font-weight: 500;
			color: var(--color-txt-2);
		}

		.container-shortcuts {
			width: 100%;
			display: flex;
			margin-top: 10px;
			flex-wrap: wrap;
			gap: 12px;

			@media (max-width: 470px) {
				flex-wrap: nowrap;
				overflow-x: auto;
				padding-bottom: 10px;
				gap: 6px;
				padding-right: 6px;
			}

			.shortcut {
				min-width: calc((100% / 2) - 6px);
				display: flex;
				align-items: center;
				justify-content: center;
				padding: 15px 12px;
				cursor: pointer;
				box-shadow: 0 2px 3px var(--color-box-shadow-3);
				transition: box-shadow 0.4s $transition;
				border-radius: 13px;
				font-weight: 500;

				&:hover {
					box-shadow: 0 2px 3px rgba(7, 82, 163, 0);
				}

				&.low {
					background-color: rgba(var(--rgb-primary), .1);
				}

				&.medium {
					background-color: rgba(var(--rgb-primary), .25);
				}

				&.high {
					background-color: rgba(var(--rgb-primary), .45);
				}

				@media (max-width: 600px) {
					font-size: 14px;
				}
				@media (max-width: 470px) {
					width: 100%;
					min-width: 100px;
				}

			}

		}

	}

	.container-numpad {
		width: 50%;
		display: flex;
		align-items: center;
		justify-content: flex-end;
		padding: 0 0 0 20px;

		:deep(.numpad) {
			@media (max-width: 610px) {
				gap: 5px;

				.row {
					gap: 5px;

					.btn {
						width: 60px;
						height: 60px;
						border-radius: 18px;

						.btn-inner {
							border-radius: 15px;
							width: 77%;
							height: 77%;
							font-size: 36px;
						}

						&.action .btn-inner {
							font-size: 32px;
						}

						&.delete .btn-inner {
							font-size: 30px;
						}
					}
				}
			}
		}

	}

	@media(max-width: 470px) {
		flex-direction: column;

		.container-info {
			width: 100%;
			margin-bottom: 20px;
		}

		.container-numpad {
			width: 100%;
			padding: 0;
			justify-content: center;
		}

	}
}
</style>